// @flow
import * as React from 'react'
import { isMobile, isIOS, isAndroid } from 'react-device-detect'
import { colors } from '@components/shared/colors'
import AppStoreBadgeButtons from '@components/shared/AppStoreBadgeButtons'
import { CTAPrimary, CTASecondary } from '@components/shared/buttons'
import { iOSBranchLink, AndroidBranchLink } from '../../../helpers/utils'
import type {
  CtaDataType,
  CtaType,
  PaywallCtaType
} from '../../../services/contentfulMarketingLandingPages'

export type PropsType = {
  cta: CtaDataType,
  isSmall: boolean
}
export type DispatchPropsType = {
  toggleModal: () => void,
  setHeaderCopy: (paywallCta: PaywallCtaType) => void
}

export default function SharedCta (
  props: PropsType & DispatchPropsType
): React.Node {
  if (props.cta.type === 'paywallCta' && props.cta.paywallCta) {
    const handleClick = (): void => {
      if (props.cta.paywallCta) {
        props.setHeaderCopy(props.cta.paywallCta)
      }

      props.toggleModal()
    }

    return PaywallButton(props.cta.paywallCta, props.isSmall, handleClick)
  } else if (props.cta.type === 'cta' && props.cta.cta) {
    return DownLoadTheApp(props.cta.cta, props.isSmall)
  } else {
    return null
  }
}

function PaywallButton (
  cta: PaywallCtaType,
  isSmall: boolean,
  onClick: () => void
): React.Node {
  const { ctaText, ctaColor } = cta

  return getButton(ctaText, ctaColor, isSmall, onClick)
}

function DownLoadTheApp (cta: CtaType, isSmall: boolean): React.Node {
  const {
    ctaUrl,
    ctaText,
    ctaColor,
    downloadTheAppOverride,
    downloadTheAppOverrideText,
    openOnNewTab,
    useAppStoreBadges
  } = cta

  if (useAppStoreBadges) {
    return <AppStoreBadgeButtons />
  }

  const target = openOnNewTab ? '_blank' : '_self'

  const [webLink, setWebLink] = React.useState(ctaUrl)
  const [buttonText, setButtonText] = React.useState(ctaText)

  React.useEffect((): void => {
    if (window) {
      if (downloadTheAppOverride) {
        if (isAndroid && isMobile) {
          setWebLink(AndroidBranchLink)
          setButtonText(downloadTheAppOverrideText || 'Get the App')
        }
        if (isIOS && isMobile) {
          setWebLink(iOSBranchLink)
          setButtonText(downloadTheAppOverrideText || 'Get the App')
        }
      }
    }
  }, [])

  const onClick = (e: any): void => {
    e.preventDefault()
    if (typeof window !== 'undefined') {
      window.open(webLink, target)
    }
  }

  return getButton(buttonText, ctaColor, isSmall, onClick)
}

const getButton = (
  buttonText: string,
  ctaColor: string,
  isSmall: boolean,
  onClick?: (e: any) => void
): React.Node => {
  let buttonMinWidth = 232
  let height = '40px'
  let padding = '10px 24px'
  if (isSmall) {
    buttonMinWidth = 100
    height = 'unset'
    padding = '8px 12px'
  }
  switch (ctaColor) {
    case 'Primary Orange':
      return (
        <CTAPrimary
          minWidth={buttonMinWidth}
          height={height}
          padding={padding}
          onClick={onClick}
        >
          {buttonText}
        </CTAPrimary>
      )
    case 'Primary Blue':
      return (
        <CTAPrimary
          minWidth={buttonMinWidth}
          height={height}
          padding={padding}
          backgroundColor={colors.blue}
          onClick={onClick}
        >
          {buttonText}
        </CTAPrimary>
      )
    case 'Secondary Sand':
      return (
        <CTASecondary
          minWidth={buttonMinWidth}
          height={height}
          padding={padding}
          onClick={onClick}
        >
          {buttonText}
        </CTASecondary>
      )
    case 'Secondary Orange':
      return (
        <CTASecondary
          minWidth={buttonMinWidth}
          height={height}
          padding={padding}
          color={colors.orange}
          onClick={onClick}
        >
          {buttonText}
        </CTASecondary>
      )
    default:
      return null
  }
}
